import axios from '@/lib/axios';

export interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export class PushNotificationService {
  private static instance: PushNotificationService;
  private registration: ServiceWorkerRegistration | null = null;

  private constructor() {}

  public static getInstance(): PushNotificationService {
    if (!PushNotificationService.instance) {
      PushNotificationService.instance = new PushNotificationService();
    }
    return PushNotificationService.instance;
  }

  async initialize(): Promise<boolean> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('Push notifications are not supported');
      return false;
    }

    try {
      // Register service worker
      this.registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });

      console.log('Service Worker registered successfully');

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      return true;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return false;
    }
  }

  async requestNotificationPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return 'denied';
    }

    let permission = Notification.permission;

    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }

    return permission;
  }

  /**
   * Subscribes to push. Throws on failure rather than returning null - callers need the
   * reason to show the user, and a silent null here previously made a failed setup look
   * identical to a successful one.
   */
  async subscribeToPush(vapidPublicKey: string): Promise<PushSubscriptionData> {
    // Fall back to the live registration: this.registration is only populated by
    // initialize(), so a re-entrant caller could otherwise see it as null.
    const registration = this.registration ?? await navigator.serviceWorker.getRegistration('/');
    if (!registration) {
      throw new Error('Service Worker is not registered, cannot subscribe to push');
    }

    const applicationServerKey = this.urlBase64ToUint8Array(vapidPublicKey);

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey as BufferSource
    });

    return {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
        auth: this.arrayBufferToBase64(subscription.getKey('auth')!)
      }
    };
  }

  /**
   * Registers the subscription with our API. Safe to call repeatedly - the server
   * upserts on (userId, endpoint), which is what lets the app self-heal a missing row.
   */
  async saveSubscriptionToServer(subscription: PushSubscriptionData): Promise<void> {
    await axios.post('/push-notifications/subscribe', {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: subscription.keys.p256dh,
        auth: subscription.keys.auth
      }
    });
  }

  async getExistingSubscription(): Promise<PushSubscriptionData | null> {
    const registration = this.registration ?? await navigator.serviceWorker.getRegistration('/');
    if (!registration) {
      return null;
    }

    try {
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        return null;
      }

      return {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
          auth: this.arrayBufferToBase64(subscription.getKey('auth')!)
        }
      };
    } catch (error) {
      console.error('Failed to get existing subscription:', error);
      return null;
    }
  }

  /**
   * Unsubscribes in the browser *and* removes the row from our API. Dropping only the
   * browser side leaves an orphaned server row that keeps being sent to until a push
   * fails with 410 Gone.
   */
  async unsubscribeFromPush(): Promise<boolean> {
    const registration = this.registration ?? await navigator.serviceWorker.getRegistration('/');
    if (!registration) {
      return false;
    }

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      return false;
    }

    const { endpoint } = subscription;
    await subscription.unsubscribe();

    // The API exposes this as DELETE with endpoint as a query string parameter.
    await axios.delete('/push-notifications/unsubscribe', { params: { endpoint } });

    return true;
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
}

export default PushNotificationService;
